/**
 * Cross-platform SMS URL builder for iOS and Android.
 *
 * iOS ≤ 11 requires "sms:PHONE&body=MSG" (ampersand separator)
 * iOS 12+ accepts both "?body=" and "&body="
 * Android always accepts "sms:PHONE?body=MSG"
 * iOS also supports "sms:/open?addresses=P1,P2&body=MSG" for multi-recipient
 *
 * We detect iOS explicitly and pick the safest format.
 */

export const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
};

/**
 * Build an SMS URL that works on both iOS and Android.
 * @param {string|string[]} phones Single number or list.
 * @param {string} body Message body (will be URL-encoded).
 * @returns {string} sms:... URL suitable for <a href="…">.
 */
export const buildSmsUrl = (phones, body) => {
  const list = Array.isArray(phones) ? phones : [phones];
  const cleaned = list.filter(Boolean).map((p) => String(p).trim()).filter(Boolean);
  const encodedBody = encodeURIComponent(body || "");
  const ios = isIOS();

  if (cleaned.length === 0) {
    return ios ? `sms:&body=${encodedBody}` : `sms:?body=${encodedBody}`;
  }

  if (cleaned.length === 1) {
    // Single recipient — universal format
    const sep = ios ? "&" : "?";
    return `sms:${cleaned[0]}${sep}body=${encodedBody}`;
  }

  // Multiple recipients
  if (ios) {
    // Native iOS multi-recipient scheme
    return `sms:/open?addresses=${encodeURIComponent(cleaned.join(","))}&body=${encodedBody}`;
  }
  // Android: comma-separated in the path
  return `sms:${cleaned.join(",")}?body=${encodedBody}`;
};
